import React from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import ImageTag from '../../../../shared/ImageTag/ImageTag';
import LinkTo from '../../../../shared/LinkTo/LinkTo';
import Link from '../../../../shared/Link/Link';
import useBrand from '../../../../../hooks/useBrand';
import { useRootSelector } from '../../../../../store/hooks';
import { selectOptions } from '../../../../../store/features/options';
import { selectPageConfig } from '../../../../../store/features/pageConfig';
import { selectSiteHeaderData } from '../../../../../store/features/siteHeader';
import { selectSiteFooter } from '../../../../../store/features/siteFooter';
import PalmFooterGeneralLinks from './PalmFooterGeneralLinks';
import PalmFooterCopyright from './PalmFooterCopyright';
import PalmMagazineFooterSocialList from './PalmMagazineFooterSocialList';
import { siteLinks, siteDescription } from './constants';

import './PalmMagazineFooter.scss';

export interface PalmMagazineFooterProps {
  footerVariant: 'Palm' | 'AvIcnExpo';
}

export function PalmMagazineFooter(props: PalmMagazineFooterProps) {
  const { footerVariant } = props;
  const siteOptions = useRootSelector(selectOptions);
  const { sitePath } = useRootSelector(selectPageConfig);
  const { title } = useRootSelector(selectSiteHeaderData);
  const siteFooter = useRootSelector(selectSiteFooter);
  const { secondaryColor } = useBrand();
  const { t } = useTranslation();

  const { whiteLogo, social = [] } = siteFooter;
  const {
    brand: {
      whiteLogo: { path: whiteLogoPath } = {},
      logo: { path: logoPath } = {},
    } = {},
  } = siteOptions;

  const logoSrc = whiteLogo ? whiteLogoPath : logoPath;
  const footerLogo = logoSrc ? (
    <LinkTo
      to={'/' + sitePath + '/'}
      className="c-palm-magazine-footer__logo-link"
    >
      <ImageTag
        src={logoSrc}
        alt={title}
        lazy={false}
        imgixParams="h=180"
        className="c-palm-magazine-footer__logo"
      />
    </LinkTo>
  ) : null;
  const getTitle = (title: string) => t(title);

  const classNameTextAvIcnExpo = cn({
    'c-palm-magazine-footer__text': footerVariant === 'AvIcnExpo',
  });

  const classNameLogoWrapperAvIcnExpo = cn({
    'c-palm-magazine-footer__av-icn-expo-logo-wrapper':
      footerVariant === 'AvIcnExpo',
  });

  return (
    <div
      className="c-palm-magazine-footer"
      style={{ backgroundColor: secondaryColor }}
    >
      <div className="container c-palm-magazine-footer__upper">
        <div className="row">
          <div className="col-12 col-xs-12 col-sm-6 col-lg-5">
            <div className="c-palm-magazine-footer__column">
              {footerLogo && (
                <div className={classNameLogoWrapperAvIcnExpo}>
                  {footerLogo}
                </div>
              )}
            </div>
            {footerVariant === 'AvIcnExpo' && (
              <div className="c-palm-magazine-footer__column">
                <div className="c-palm-magazine-footer__av-icn-text">
                  28-30 May 2026 | Bombay Exhibition Center, Mumbai, India
                </div>
              </div>
            )}
            {social.length > 0 && (
              <div className="c-palm-magazine-footer__social-wrapper">
                <PalmMagazineFooterSocialList
                  socialLinks={social}
                  footerVariant={footerVariant}
                />
              </div>
            )}
          </div>
          <div className="col-12 col-xs-12 col-sm-6 col-lg-4">
            <div className="c-palm-magazine-footer__column">
              <h3 className="c-palm-magazine-footer__site-links__heading">
                {siteDescription[footerVariant].title}
                <div
                  className="c-palm-magazine-footer__site-links__heading-underline"
                  style={{ borderColor: secondaryColor }}
                />
              </h3>
              <div className={classNameTextAvIcnExpo}>
                {siteDescription[footerVariant].text}
              </div>
            </div>
          </div>
          <div className="col-12 col-xs-12 col-sm-12 col-lg-3">
            <div className="c-palm-magazine-footer__site-links">
              <h3 className="c-palm-magazine-footer__site-links__heading">
                {t(siteLinks[footerVariant].title)}
                <div
                  className="c-palm-magazine-footer__site-links__heading-underline"
                  style={{ borderColor: secondaryColor }}
                />
              </h3>
              <ul>
                {siteLinks[footerVariant].items.map((item, i) => (
                  <li
                    key={i + item.title}
                    className="c-palm-magazine-footer__site-links__item"
                  >
                    <Link
                      to={item.href}
                      event={sitePath}
                      className="c-palm-magazine-footer__site-links__link"
                      target={item.target}
                    >
                      {getTitle(item.title)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
      <PalmFooterCopyright />
      <PalmFooterGeneralLinks />
    </div>
  );
}

export default PalmMagazineFooter;
